import { useRef, useCallback, useState } from 'react';

interface UseSwipeToCloseProps {
  onClose: () => void;
  threshold?: number; // Minimum distance to trigger close
  direction?: 'down' | 'up' | 'left' | 'right';
}

export const useSwipeToClose = ({
  onClose,
  threshold = 100,
  direction = 'down',
}: UseSwipeToCloseProps) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startX = useRef(0);
  const currentY = useRef(0);
  const currentX = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    startY.current = touch.clientY;
    startX.current = touch.clientX;
    currentY.current = touch.clientY;
    currentX.current = touch.clientX;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    currentY.current = touch.clientY;
    currentX.current = touch.clientX;

    let offset = 0;
    if (direction === 'down') {
      offset = Math.max(0, currentY.current - startY.current);
    } else if (direction === 'up') {
      offset = Math.max(0, startY.current - currentY.current);
    } else if (direction === 'left') {
      offset = Math.max(0, startX.current - currentX.current);
    } else if (direction === 'right') {
      offset = Math.max(0, currentX.current - startX.current);
    }

    setDragOffset(offset);
  }, [isDragging, direction]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    if (dragOffset > threshold) {
      onClose();
    }

    setDragOffset(0);
  }, [dragOffset, threshold, onClose]);

  const getTransform = useCallback(() => {
    if (!isDragging || dragOffset === 0) return undefined;

    switch (direction) {
      case 'down':
        return `translateY(${dragOffset}px)`;
      case 'up':
        return `translateY(-${dragOffset}px)`;
      case 'left':
        return `translateX(-${dragOffset}px)`;
      case 'right':
        return `translateX(${dragOffset}px)`;
      default:
        return undefined;
    }
  }, [isDragging, dragOffset, direction]);

  const getOpacity = useCallback(() => {
    if (!isDragging || dragOffset === 0) return 1;
    return Math.max(0.3, 1 - (dragOffset / (threshold * 2)));
  }, [isDragging, dragOffset, threshold]);

  return {
    dragOffset,
    isDragging,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    style: {
      transform: getTransform(),
      transition: isDragging ? 'none' : 'transform 0.2s ease-out',
    },
    overlayStyle: {
      opacity: getOpacity(),
      transition: isDragging ? 'none' : 'opacity 0.2s ease-out',
    },
  };
};
